<template>
  <div>
    <vs-collapse accordion type="margin" class="px-0" :key="collapseKey">
      <draggable-container :disable-index="noConditionIndex"
                           :items="caseTransitions"
                           :is-disabled="openCaseIndex !== -1"
                           @change-order="reorderCases"
                           @is-dragging="draggingStatus => isDragging = draggingStatus">
        <vs-collapse-item v-for="(transition, i) in caseTransitions"
                          :ref="`case${i}`"
                          not-arrow
                          :open="isOpen(i)"
                          :key="i + collapseKey"
                          class="shadow-none rounded editable-case my-1 p-0 border-solid border"
                          :class="resolveItemClasses(i)">
          <div slot="header"
               @click="() => toggleCaseOpened(i, transition.state)"
               class="flex justify-center items-center text-sm mt-3 mx-5 py-3 px-3 editable-case-header select-none border border-solid rounded-full"
               :class="resolveHeaderClasses(i)"
               @mouseover="isHover = !isCaseNoCondition(i)"
               @mouseleave="isHover = false"
          >

            <vs-icon v-if="openCaseIndex === -1 && isHover && !isCaseNoCondition(i)"
                     icon="drag_indicator" size="1.5rem"
                     class="text-gray-dark absolute left-0 ml-10"
            />

            <div class="cursor-pointer text-gray-dark">
              <condition-case-label :condition="transition.condition" :show-text="true"/>
            </div>
          </div>

          <condition-case-editor v-if="openCaseIndex === i"
                                 :transition="transition"
                                 @update-case="updatedCase => updateCase(i, updatedCase)"
                                 @delete-case="() => deleteCase(i)"/>
        </vs-collapse-item>
      </draggable-container>
    </vs-collapse>
  </div>
</template>

<script>
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import ConditionCaseEditor from './ConditionCaseEditor.vue'
import conversationModule from '@/store/modules/conversation.module'
import templateModule from '@/store/modules/template.module'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import DraggableContainer from '../DraggableContainer.vue'
import ConditionCaseLabel from './ConditionCaseLabel.vue'

export default {
  name: 'ConditionCasesSection',

  components: {
    ConditionCaseEditor,
    DraggableContainer,
    ConditionCaseLabel
  },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    caseIndex: {
      immediate: true,
      handler(index) {
        this.openCase(index)
      }
    }
  },

  data() {
    return {
      isDragging: false,
      isHover: false,
      openCaseIndex: -1
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    focus: chatBubbleModule.sectionFocus,
    collapseKey: chatBubbleModule.collapseKey,

    conditionStateId() {
      return this.bubbleSelected.stateId
    },

    caseTransitions() {
      return conversationModule.getStateTransitions(this.conditionStateId)
    },

    caseIndex() {
      return this.bubbleSelected.caseSelected
    },

    noConditionIndex() {
      return this.caseTransitions.length - 1
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    }
  },

  methods: {
    isCaseNoCondition(i) {
      return i === this.caseTransitions.length - 1
    },

    resolveItemClasses(i) {
      if (this.isOpen(i)) return ['border-soft']

      const classes = ['border-transparent']
      if (this.isDragging) classes.push('cursor-grabbing')

      return classes
    },

    resolveHeaderClasses(i) {
      if (this.isOpen(i)) return ['mb-3', 'border-focus']

      const classes = ['border-soft']
      if (this.isCaseNoCondition(i)) return classes

      if (this.isDragging) {
        classes.push('cursor-grabbing')
      } else {
        classes.push('cursor-grab')
      }

      return classes
    },

    reorderCases(fromIndex, toIndex) {
      this.previewController.reorderTransitions(this.conditionStateId, fromIndex, toIndex, templateModule.getTemplateId())
    },

    isOpen(i) {
      return i === this.openCaseIndex
    },

    toggleCaseOpened(i) {
      // the content of vs-collapse-item is at the moment of item opening missing in DOM and scrollHeight is being called
      // on undefined property. Seems scrollHeight isn't really required for anything, and 0 is there just to prevent errors
      if (this.$refs[`case${i}`]?.[0]) {
        this.$refs[`case${i}`][0].$refs.content = { scrollHeight: 0 }
      }

      if (this.isOpen(i)) {
        this.closeCaseCollapses()
        return
      }
      chatBubbleModule.selectConditionSection(this.conditionStateId, i)
      this.openCase(i)
    },

    openCase(i) {
      this.openCaseIndex = i
      this.resetCollapseKey()
    },

    deleteCase(i) {
      this.previewController.removeCaseFromCondition(this.conditionStateId, i, templateModule.getTemplateId())
      this.closeCaseCollapses()
    },

    updateCase(i, updatedCase) {
      const transition = { state: updatedCase.stateId, condition: updatedCase.condition }
      this.previewController.updateCase(this.conditionStateId, transition, i, templateModule.getTemplateId())

      this.$nextTick(() => {
        const caseKey = `case${i}`
        this.$refs[caseKey][0].changeHeight()
      })
    },

    closeCaseCollapses() {
      this.resetCollapseKey()
      this.openCaseIndex = -1
    },

    resetCollapseKey() {
      this.collapseKey = Math.random()
    }
  }
}
</script>

<style scoped lang="scss">

.editable-case.open-item {
  // noinspection CssInvalidFunction - theme() belongs to TailWind
  box-shadow: 0 10px 30px theme('colors.gray-a10') !important;
}

.editable-case.open-item header {
  padding: 0.5rem !important;
}

.editable-case.open-item > header > .editable-case-header {
  border: 1px solid;
  // noinspection CssInvalidFunction - theme() belongs to TailWind
  border-color: theme('colors.focus') !important;
  border-radius: 4px;
}

</style>
